<script lang="ts">
	import { pinchZoom } from '#lib/pinch-zoom.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import ArrowLeft from 'virtual:icons/material-symbols/arrow-back';
	import ArrowRight from 'virtual:icons/material-symbols/arrow-forward';
	import CloseIcon from 'virtual:icons/material-symbols/close-rounded';

	interface Props {
		/** With more than one, the lightbox becomes a carousel. */
		src: string | string[];
		alt?: string;
	}
	let { src, alt = '' }: Props = $props();

	const modalCtx = getModalContext();

	const srcs = $derived(Array.isArray(src) ? src : [src]);
	let index = $state(0);

	// Same gestures as the gallery carousel on antonio-website, but on a track
	// of all images instead of route navigation.
	let dragX = $state(0);
	let isDragging = $state(false);

	function go(dir: 1 | -1) {
		index = Math.max(0, Math.min(index + dir, srcs.length - 1));
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'ArrowLeft') {
			go(-1);
		}
		if (e.key === 'ArrowRight') {
			go(1);
		}
	}

	// Svelte registers ontouchmove as a passive listener, so preventDefault()
	// in it would silently no-op.
	function carouselTouch(node: HTMLElement) {
		let touchStartX = 0;
		let touchStartY = 0;
		let isHorizontal: boolean | null = null;

		function handleTouchStart(e: TouchEvent) {
			touchStartX = e.touches[0].clientX;
			touchStartY = e.touches[0].clientY;
			isHorizontal = null;
		}

		function handleTouchMove(e: TouchEvent) {
			// pinchZoom, listening on the image, already took it: a pinch, or a
			// pan of a zoomed image.
			if (e.defaultPrevented || e.touches.length > 1) {
				return;
			}
			const dx = e.touches[0].clientX - touchStartX;
			const dy = e.touches[0].clientY - touchStartY;

			if (isHorizontal === null) {
				if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
					isHorizontal = Math.abs(dx) > Math.abs(dy);
				}
				return;
			}

			if (!isHorizontal) {
				return;
			}
			// dragging toward an image that doesn't exist (list boundary) — ignore
			if ((dx > 0 && index === 0) || (dx < 0 && index === srcs.length - 1)) {
				return;
			}
			e.preventDefault();
			isDragging = true;
			dragX = dx;
		}

		function handleTouchEnd() {
			isHorizontal = null;
			if (!isDragging) {
				return;
			}
			isDragging = false;
			if (Math.abs(dragX) > node.offsetWidth * 0.25) {
				go(dragX > 0 ? -1 : 1);
			}
			dragX = 0;
		}

		node.addEventListener('touchstart', handleTouchStart);
		node.addEventListener('touchmove', handleTouchMove, { passive: false });
		node.addEventListener('touchend', handleTouchEnd);
		node.addEventListener('touchcancel', handleTouchEnd);
		return () => {
			node.removeEventListener('touchstart', handleTouchStart);
			node.removeEventListener('touchmove', handleTouchMove);
			node.removeEventListener('touchend', handleTouchEnd);
			node.removeEventListener('touchcancel', handleTouchEnd);
		};
	}
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="image-lightbox">
	<div
		class="track"
		{@attach carouselTouch}
		style:transform="translateX(calc({-index * 100}% + {dragX}px))"
		style:transition={isDragging ? 'none' : 'transform 0.3s ease-out'}
	>
		{#each srcs as imageSrc, i (i)}
			<div class="slide">
				<img src={imageSrc} {alt} {@attach pinchZoom} />
			</div>
		{/each}
	</div>
	<button
		type="button"
		class="icon-button glass close-button"
		onclick={() => modalCtx.closeModal()}
		aria-label="Close"
	>
		<CloseIcon />
	</button>
	{#if srcs.length > 1}
		<button
			type="button"
			class="icon-button glass nav-button prev"
			disabled={index === 0}
			onclick={() => go(-1)}
			aria-label="Previous image"
		>
			<ArrowLeft />
		</button>
		<button
			type="button"
			class="icon-button glass nav-button next"
			disabled={index === srcs.length - 1}
			onclick={() => go(1)}
			aria-label="Next image"
		>
			<ArrowRight />
		</button>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.image-lightbox {
		@include lib.flex-row();
		@include lib.flex-align(center, center);
		position: relative;
		height: 100%;
		width: 100%;
		overflow: hidden;

		.track {
			@include lib.flex-row();
			height: 100%;
			width: 100%;
			will-change: transform;
			touch-action: pan-y;

			.slide {
				@include lib.flex-row();
				@include lib.flex-align(center, center);
				flex: 0 0 100%;
				height: 100%;
				overflow: hidden;

				img {
					max-height: 100%;
					max-width: 100%;
					object-fit: contain;
					touch-action: none;
				}
			}
		}

		.close-button {
			position: absolute;
			top: 1rem;
			right: 1rem;
		}

		.nav-button {
			position: absolute;
			top: 50%;
			transform: translateY(-50%);

			&.prev {
				left: 1rem;
			}
			&.next {
				right: 1rem;
			}
		}
	}
</style>
